import React, { useState } from 'react';
import { Check, Copy, Download, Eye, RotateCcw } from 'lucide-react';

import Seo from '@/components/Seo';
import Logo from '@/components/brand/Logo';
import LogoMark, { setLogoConcept, useLogoConcept } from '@/components/brand/LogoMark';
import { useI18n } from '@/i18n/LanguageContext';
import { BRAND_NAME } from '@/lib/brand';
import {
  LOGO_CONCEPTS,
  DEFAULT_CONCEPT_ID,
  LogoConcept,
  conceptDataUrl,
  conceptSvgDocument,
  getConcept,
} from '@/lib/logoConcepts';

/**
 * BRAND STUDIO.
 *
 * A logo cannot be judged from one 200px render on a white slide. This page
 * shows every candidate in the conditions that actually break marks: 16px
 * favicon, app icon, inline on white, inline on navy, and inside the real
 * header lockup. "Preview on site" swaps the live header/footer mark for this
 * browser only, so a decision can be made against the real product.
 */
const Brand: React.FC = () => {
  const { lang } = useI18n();
  const es = lang === 'es';
  const activeId = useLogoConcept();
  const [copied, setCopied] = useState<string | null>(null);

  const L = {
    title: es ? 'Estudio de marca' : 'Brand studio',
    sub: es
      ? 'Cinco conceptos de logotipo para GIGGONE, evaluados en las condiciones que realmente rompen una marca: favicon de 16px, icono de app, fondo claro y fondo oscuro.'
      : 'Five logo concepts for GIGGONE, reviewed in the conditions that actually break a mark: 16px favicon, app icon, light background and dark background.',
    idea: es ? 'La idea' : 'The idea',
    why: es ? 'Por qué funciona' : 'Why it holds up',
    favicon: es ? 'Favicon 16 / 32px' : 'Favicon 16 / 32px',
    appIcon: es ? 'Icono de app' : 'App icon',
    onLight: es ? 'Sobre fondo claro' : 'On light',
    onDark: es ? 'Sobre fondo oscuro' : 'On dark',
    preview: es ? 'Previsualizar en el sitio' : 'Preview on site',
    previewing: es ? 'Previsualizando' : 'Previewing',
    shipped: es ? 'Marca activa' : 'Shipped mark',
    copy: es ? 'Copiar URL de favicon' : 'Copy favicon URL',
    copied: es ? 'Copiado' : 'Copied',
    download: es ? 'Descargar SVG' : 'Download SVG',
    reset: es ? 'Volver a la marca activa' : 'Reset to shipped mark',
    note: es
      ? 'La previsualización solo afecta a este navegador. Para publicar un concepto, cambie DEFAULT_CONCEPT_ID en src/lib/logoConcepts.ts y pegue la URL del favicon en index.html.'
      : 'Preview affects this browser only. To ship a concept, change DEFAULT_CONCEPT_ID in src/lib/logoConcepts.ts and paste its favicon URL into index.html.',
    priority: es ? 'Prioridad' : 'Priority',
  };

  // The three concepts the brief flagged as priorities, surfaced as a badge so
  // a reviewer can tell at a glance which are the shortlist.
  const PRIORITY: Record<string, string> = {
    link: es ? 'Enlace de marketplace' : 'Marketplace link',
    gate: es ? 'Icono propio' : 'Signature icon',
    climb: es ? 'Flecha de crecimiento' : 'Growth arrow',
  };

  const copyFavicon = async (c: LogoConcept) => {
    const href = conceptDataUrl(c, 'tile');
    try {
      await navigator.clipboard.writeText(href);
      setCopied(c.id);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      window.prompt(L.copy, href);
    }
  };

  const downloadSvg = (c: LogoConcept) => {
    const blob = new Blob([conceptSvgDocument(c, 'tile')], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `giggone-${c.id}-mark.svg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-brand-canvas">
      <Seo
        title={L.title}
        description={L.sub}
        /* Internal design tool — kept out of the index so it never competes
           with a marketplace page in search results. */
        noindex
      />

      {/* ---------------- HEADER ---------------- */}
      <section className="bg-brand-navy py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Logo tone="dark" size={44} concept={getConcept(activeId)} />
          <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">{L.title}</h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-300">{L.sub}</p>
          <p className="mt-4 max-w-2xl text-xs leading-relaxed text-slate-400">{L.note}</p>
          {activeId !== DEFAULT_CONCEPT_ID && (
            <button
              onClick={() => setLogoConcept(DEFAULT_CONCEPT_ID)}
              className="mt-5 inline-flex items-center gap-2 rounded-lg border border-white/25 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-white/10"
            >
              <RotateCcw className="h-4 w-4" /> {L.reset}
            </button>
          )}
        </div>
      </section>

      {/* ---------------- CONCEPT GRID ---------------- */}
      <section className="py-14">
        <div className="mx-auto grid max-w-7xl gap-6 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          {LOGO_CONCEPTS.map((c, i) => {
            const isActive = c.id === activeId;
            return (
              <article
                key={c.id}
                className={`flex flex-col overflow-hidden rounded-3xl border bg-white shadow-card transition-shadow ${
                  isActive ? 'border-brand-orange ring-2 ring-orange-200' : 'border-slate-200'
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-100 p-6">
                  <div className="flex items-center gap-4">
                    <LogoMark concept={c} variant="tile" size={64} className="rounded-2xl" />
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                        {String(i + 1).padStart(2, '0')}
                      </p>
                      <h2 className="text-xl font-extrabold tracking-tight text-slate-900">
                        {es ? c.name_es : c.name}
                      </h2>
                      {PRIORITY[c.id] && (
                        <span className="mt-1 inline-block rounded-full bg-blue-50 px-2.5 py-0.5 text-[11px] font-bold text-brand-blue ring-1 ring-blue-200">
                          {L.priority}: {PRIORITY[c.id]}
                        </span>
                      )}
                    </div>
                  </div>
                  {c.id === DEFAULT_CONCEPT_ID && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700 ring-1 ring-emerald-200">
                      <Check className="h-3.5 w-3.5" /> {L.shipped}
                    </span>
                  )}
                </div>

                <div className="flex-1 space-y-5 p-6">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{L.idea}</p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-700">{es ? c.idea_es : c.idea}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{L.why}</p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">
                      {es ? c.rationale_es : c.rationale}
                    </p>
                  </div>

                  {/* ---- SCALE + BACKGROUND TESTS ---- */}
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-2xl border border-slate-200 bg-brand-canvas p-4">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{L.favicon}</p>
                      <div className="mt-3 flex items-end gap-4">
                        <LogoMark concept={c} variant="tile" size={16} className="rounded-[3px]" />
                        <LogoMark concept={c} variant="tile" size={32} className="rounded-md" />
                      </div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-brand-canvas p-4">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{L.appIcon}</p>
                      <div className="mt-3">
                        <LogoMark concept={c} variant="tile" size={48} className="rounded-xl shadow-card" />
                      </div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{L.onLight}</p>
                      <div className="mt-3 text-slate-900">
                        <Logo concept={c} tone="light" size={32} />
                      </div>
                    </div>
                    <div className="rounded-2xl border border-slate-800 bg-brand-navy p-4">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{L.onDark}</p>
                      <div className="mt-3 flex items-center gap-3 text-white">
                        <LogoMark concept={c} variant="glyph" size={32} />
                        {/* Glyph variant + wordmark: proves the mark reads
                            without its navy tile, which is how it appears in
                            email signatures, print and dark-mode surfaces. */}

                        <span className="text-lg font-extrabold leading-none tracking-tight text-white">
                          {BRAND_NAME}
                          <span className="text-brand-orange">.</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ---- ACTIONS ---- */}
                <div className="flex flex-wrap gap-2 border-t border-slate-100 bg-slate-50/60 p-4">
                  <button
                    onClick={() => setLogoConcept(c.id)}
                    disabled={isActive}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2.5 text-sm font-bold transition-colors ${
                      isActive
                        ? 'cursor-default bg-orange-100 text-orange-700'
                        : 'bg-brand-navy text-white hover:bg-slate-800'
                    }`}
                  >
                    <Eye className="h-4 w-4" /> {isActive ? L.previewing : L.preview}
                  </button>
                  <button
                    onClick={() => copyFavicon(c)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-800 transition-colors hover:bg-slate-50"
                  >
                    {copied === c.id ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                    {copied === c.id ? L.copied : L.copy}
                  </button>
                  <button
                    onClick={() => downloadSvg(c)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-800 transition-colors hover:bg-slate-50"
                  >
                    <Download className="h-4 w-4" /> {L.download}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default Brand;
